import React, { useContext as useContextCore } from 'react'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import reject from 'lodash/reject'
import type { ReactNode } from 'react'
import type RGL from 'react-grid-layout'
import type { DashboardSetting } from './useDashboardSetting'
import { useDashboardSetting } from './useDashboardSetting'

export type DashboardWidgetProps = { height: number }
export type DashboardWidget = (props: DashboardWidgetProps) => ReactNode

export type DashboardContextType = {
  setting: DashboardSetting
  loading: boolean
  saving: boolean
  widgetNames: Record<string, string>
  widgetLabels?: Record<string, string>
  resolveWidget: (id: string) => DashboardWidget | undefined
  onUpdateLayout: (layout: RGL.Layout[]) => Promise<void>
  onRemoveWidget: (id: string) => Promise<void>
  onAddWidget: (id: string) => Promise<void>
  onReset: () => Promise<void>
}

const context = React.createContext<DashboardContextType>({} as DashboardContextType)

export type DashboardProviderProps = {
  children: React.ReactNode
  defaultLayout: DashboardSetting
  /** Every widget id the app offers, as the values of this map. */
  widgetNames: Record<string, string>
  /** The toolbox label per widget id; an id with no entry is shown humanized. */
  widgetLabels?: Record<string, string>
  /** An id this returns nothing for renders neither on the grid nor in the toolbox. */
  resolveWidget: (id: string) => DashboardWidget | undefined
}

export const DashboardProvider = ({
  children,
  defaultLayout,
  widgetNames,
  widgetLabels,
  resolveWidget,
}: DashboardProviderProps) => {
  const { setting, loading, saving, save } = useDashboardSetting({ defaultLayout })

  // react-grid-layout only reports the items it was given, so a widget hidden by `resolveWidget` is carried over.
  const onUpdateLayout = (layout: RGL.Layout[]) =>
    save({ layout: [...layout, ...filter(setting.layout, (item) => !resolveWidget(item.i))] })

  const onRemoveWidget = (id: string) =>
    save({
      layout: reject(setting.layout, (item) => item.i === id),
      toolbox: compact([...setting.toolbox, find(setting.layout, (item) => item.i === id)]),
    })

  const onAddWidget = (id: string) => {
    const item = find(setting.toolbox, (i) => i.i === id) ?? find(defaultLayout.toolbox, (i) => i.i === id)
    return save({
      layout: compact([...setting.layout, item]),
      toolbox: reject(setting.toolbox, (i) => i.i === id),
    })
  }

  const onReset = () => save(defaultLayout)

  return (
    <context.Provider
      value={{
        setting,
        loading,
        saving,
        widgetNames,
        widgetLabels,
        resolveWidget,
        onUpdateLayout,
        onRemoveWidget,
        onAddWidget,
        onReset,
      }}
    >
      {children}
    </context.Provider>
  )
}

export const useDashboardContext = () => useContextCore(context)
